import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtDateShort, fmtDateTime } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./HealthTab.module.css";

const SEVERITY_STYLE = {
  SEVERE:   { bg: "#fde8e4", color: "#c0392b" },
  MODERATE: { bg: "#fdf3d4", color: "#8a6d28" },
  MILD:     { bg: "#e8f6ed", color: "#1e6f3e" },
};

export default function HealthTab({ studentId }) {
  const [allergies,  setAllergies]  = useState([]);
  const [meds,       setMeds]       = useState([]);
  const [visits,     setVisits]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/student-allergies?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/student-medications?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/clinic/visits?studentId=${studentId}&limit=50`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([allergyData, medData, visitData]) => {
        if (cancelled) return;
        setAllergies(Array.isArray(allergyData) ? allergyData : []);
        setMeds(Array.isArray(medData)          ? medData     : []);
        setVisits(Array.isArray(visitData)      ? visitData   : []);
      })
      .catch(() => { if (!cancelled) setError("Could not load health records."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [studentId]);

  const hasData = allergies.length > 0 || meds.length > 0 || visits.length > 0;

  if (loading) return <TabSpinner label="Loading health records…" />;
  if (error)   return <TabError message={error} />;
  if (!hasData) return (
    <TabEmpty
      icon="🏥"
      title="No health records"
      sub="Allergies, medications, and clinic visits will appear here once recorded by the school nurse."
    />
  );

  /* Separate active medications (no end date, or end date in the future) */
  const today      = new Date();
  const activeMeds = meds.filter(m => !m.endDate || new Date(m.endDate) >= today);
  const pastMeds   = meds.filter(m =>  m.endDate && new Date(m.endDate) <  today);

  return (
    <div className={s.root}>
      {/* ── Allergies ─────────────────────────────────────────────── */}
      {allergies.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionTitle}>
            <span aria-hidden="true">⚠️</span> Allergies
            <span className={s.count}>{allergies.length}</span>
          </div>
          <div className={s.allergyList}>
            {allergies.map((a, idx) => {
              const style = SEVERITY_STYLE[a.severity] || { bg: "#f5f5f5", color: "#888" };
              return (
                <div key={a.id} className={s.allergyCard} style={{ animationDelay: `${idx * 40}ms` }}>
                  <div className={s.allergyHead}>
                    <span className={s.allergen}>{a.allergen}</span>
                    {a.severity && (
                      <span className={s.severity} style={{ background: style.bg, color: style.color }}>
                        {a.severity}
                      </span>
                    )}
                  </div>
                  {a.notes && <div className={s.allergyNotes}>{a.notes}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Active Medications ────────────────────────────────────── */}
      {activeMeds.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionTitle}>
            <span aria-hidden="true">💊</span> Current Medications
            <span className={s.count}>{activeMeds.length}</span>
          </div>
          <MedTable meds={activeMeds} />
        </div>
      )}

      {/* ── Past Medications ─────────────────────────────────────── */}
      {pastMeds.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionTitle}>
            <span aria-hidden="true">📋</span> Past Medications
            <span className={s.count}>{pastMeds.length}</span>
          </div>
          <MedTable meds={pastMeds} past />
        </div>
      )}

      {/* ── Clinic Visits ────────────────────────────────────────── */}
      {visits.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionTitle}>
            <span aria-hidden="true">🏥</span> Clinic Visits
            <span className={s.count}>{visits.length}</span>
          </div>
          <div className={s.visitList}>
            {visits.map((v, idx) => (
              <div key={v.id} className={s.visitCard} style={{ animationDelay: `${idx * 30}ms` }}>
                <div className={s.visitDate}>{fmtDateTime(v.visitedAt)}</div>
                {v.complaint && (
                  <div className={s.visitRow}>
                    <span className={s.visitLbl}>Complaint</span>
                    <span>{v.complaint}</span>
                  </div>
                )}
                {v.treatment && (
                  <div className={s.visitRow}>
                    <span className={s.visitLbl}>Treatment</span>
                    <span>{v.treatment}</span>
                  </div>
                )}
                {v.returnedAt && (
                  <div className={s.visitRow}>
                    <span className={s.visitLbl}>Returned</span>
                    <span>{fmtDateTime(v.returnedAt)}</span>
                  </div>
                )}
                {!v.returnedAt && (
                  <div className={s.visitRow}>
                    <span className={s.stillIn}>Still in clinic</span>
                  </div>
                )}
                {v.notes && <div className={s.visitNotes}>{v.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MedTable({ meds, past = false }) {
  return (
    <div className={s.medTable}>
      {meds.map((m, idx) => (
        <div key={m.id} className={`${s.medRow} ${past ? s.medPast : ""}`} style={{ animationDelay: `${idx * 40}ms` }}>
          <div className={s.medDrug}>{m.drugName}</div>
          <div className={s.medDetails}>
            {m.dose     && <span>{m.dose}</span>}
            {m.schedule && <span>{m.schedule}</span>}
            {m.prescribedBy && <span>Rx: {m.prescribedBy}</span>}
            {m.startDate && (
              <span>
                {fmtDateShort(m.startDate)}
                {m.endDate ? ` → ${fmtDateShort(m.endDate)}` : " · Ongoing"}
              </span>
            )}
          </div>
          {m.notes && <div className={s.medNotes}>{m.notes}</div>}
        </div>
      ))}
    </div>
  );
}
